import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, Star, BadgeCheck, MapPin, Navigation, Heart, Share2, Flag, ChevronRight,
  Phone, MessageCircle, Instagram, Music2, Globe, Mail, Bike, CalendarDays, ShoppingBag,
  Armchair, Car, Wifi, PawPrint, Users, Check, Banknote, CreditCard, Wallet, Clock, Eye,
  Bed, Plane, Bus, TrainFront, Ship, Package, Truck, Facebook, Youtube, Wrench, MessageSquare } from "lucide-react";
import api, { errMsg } from "@/lib/api";
import { catMap, timeAgo } from "@/lib/categories";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { MapView } from "@/components/MapView";
import { ReviewsSection } from "@/components/ReviewsSection";
import { StopsTimeline } from "@/components/Stops";
import { HoldZoomImg, HoldZoomBox } from "@/components/HoldZoom";
import { MenuViewer } from "@/components/MenuViewer";
import { MenuBook, MenuBookViewer, bookEligible } from "@/components/MenuBook";
import PaymentMethods from "@/components/PaymentMethods";
import { toast } from "sonner";
import { ListingOffers } from "@/components/qr/ListingOffers";
import { weekFrom, openInfo } from "@/lib/hours";

const SERVICE_ICONS = {
  "Delivery": Bike, "Rezervime": CalendarDays, "Merr me vete": ShoppingBag, "Ulëse jashtë": Armchair,
  "Parking": Car, "Wi-Fi": Wifi, "Kafshë lejohen": PawPrint, "Për grupe": Users,
};
const SERVICE_EMOJI = {
  "Pa duhan": "🚭", "Po duhan": "🚬", "Kafshë": "🐕", "Pa kafshë": "🚫", "Karikim EV": "⚡", "WC": "🚻", "Qasje": "♿", "Kënd fëmijësh": "👶",
};
const PAY_ICONS = { "Para të gatshme": Banknote, "Kartelë": CreditCard, "Apple Pay": Wallet, "Google Pay": Wallet, "Tjetër": Wallet };
const TRAVEL = {
  taxi: { label: "Taksi", icon: Car }, taksi: { label: "Taksi", icon: Car }, aeroplan: { label: "Aeroplan", icon: Plane }, autobus: { label: "Autobus", icon: Bus },
  tren: { label: "Tren", icon: TrainFront }, anije: { label: "Anije", icon: Ship }, dergesa: { label: "Dërgesa", icon: Package },
  makina: { label: "Makinë me qira", icon: Car }, transfer: { label: "Transfer", icon: Truck },
};

const CONTACTS = (l) => {
  const out = [];
  if (l.phone) out.push({ k: "call", label: "Thirr", icon: Phone, href: `tel:${l.phone}`, cls: "bg-emerald-500" });
  if (l.whatsapp) out.push({ k: "whatsapp", label: "WhatsApp", icon: MessageCircle, href: `https://wa.me/${l.whatsapp.replace(/[^0-9]/g, "")}`, cls: "bg-green-500" });
  if (l.viber) out.push({ k: "viber", label: "Viber", icon: Phone, href: `viber://chat?number=${l.viber.replace(/[^0-9]/g, "")}`, cls: "bg-purple-500" });
  if (l.instagram) out.push({ k: "instagram", label: "Instagram", icon: Instagram, href: `https://instagram.com/${l.instagram.replace("@", "")}`, cls: "bg-gradient-to-br from-fuchsia-500 to-pink-500" });
  if (l.tiktok) out.push({ k: "tiktok", label: "TikTok", icon: Music2, href: `https://tiktok.com/@${l.tiktok.replace("@", "")}`, cls: "bg-slate-900" });
  if (l.website) out.push({ k: "website", label: "Web", icon: Globe, href: /^https?:\/\//i.test(l.website) ? l.website : `https://${l.website}`, cls: "bg-blue-500" });
  const d = l.data || {};
  const fb = l.facebook || d.facebook;
  if (fb) out.push({ k: "facebook", label: "Facebook", icon: Facebook, href: fb.startsWith("http") ? fb : `https://facebook.com/${fb.replace("@", "")}`, cls: "bg-blue-600" });
  if (d.youtube && !["udhetim", "dyqan"].includes(l.category)) out.push({ k: "youtube", label: "YouTube", icon: Youtube, href: d.youtube.startsWith("http") ? d.youtube : `https://youtube.com/${d.youtube}`, cls: "bg-red-600" });
  if (l.email) out.push({ k: "email", label: "Email", icon: Mail, href: `mailto:${l.email}`, cls: "bg-slate-500" });
  return out;
};

function groupHours(week) {
  const res = [];
  week.forEach((h) => {
    const label = !h.open ? "Mbyllur" : h.h24 ? "24 orë" : `${h.from} – ${h.to}`;
    const last = res[res.length - 1];
    if (last && last.label === label) last.endDay = h.day;
    else res.push({ startDay: h.day, endDay: h.day, label, open: h.open });
  });
  return res;
}

function Section({ title, action, onAction, children }) {
  return (
    <section className="px-4 mt-5">
      <div className="flex items-center justify-between mb-2">
        <h2 className="text-lg font-extrabold text-slate-900">{title}</h2>
        {action && <button onClick={onAction} className="flex items-center gap-0.5 text-sm font-semibold text-emerald-600">{action} <ChevronRight className="w-4 h-4" /></button>}
      </div>
      {children}
    </section>
  );
}

function money(v) { if (v == null || v === "") return null; const s = String(v); return s.includes("€") ? s : `${s} €`; }

function TravelBox({ icon: Icon, title, from, to, rows, badges, cta, href, testid }) {
  const items = rows.filter(([, v]) => v != null && v !== "");
  return (
    <Section title={title}>
      <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-4">
        {(from || to) && (
          <div className="flex items-center gap-2 text-lg font-extrabold text-slate-900 mb-3">
            <Icon className="w-5 h-5 text-emerald-500 shrink-0" />
            <span className="truncate">{from}{from && to ? " → " : ""}{to}</span>
          </div>
        )}
        {items.length > 0 && (
          <div className="grid grid-cols-2 gap-2">
            {items.map(([k, v]) => (
              <div key={k} className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-400">{k}</div><div className="font-semibold text-slate-800 text-sm">{String(v)}</div></div>
            ))}
          </div>
        )}
        {badges && badges.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{badges.map((b) => <span key={b} className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full">{b}</span>)}</div>}
        {href && <a data-testid={testid} href={href} target="_blank" rel="noreferrer" className="mt-3 flex items-center justify-center w-full h-11 rounded-xl bg-emerald-500 hover:bg-emerald-600 text-white font-bold active:scale-95 transition-transform">{cta}</a>}
      </div>
    </Section>
  );
}

export default function ListingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user, requireAuth } = useAuth();
  const [l, setL] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [saved, setSaved] = useState(false);
  const [imgIdx, setImgIdx] = useState(0);
  const [menuAt, setMenuAt] = useState(null);
  const [bookOpen, setBookOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuAt(null), []);
  const [reportOpen, setReportOpen] = useState(false);
  const [navOpen, setNavOpen] = useState(false);
  const [reason, setReason] = useState("spam");

  const load = useCallback(() => {
    const draft = new URLSearchParams(window.location.search).get("draft") === "1";
    api.get(draft ? `/listings/${id}/draft` : `/listings/${id}`).then((r) => {
      if (r.data.category === "punekerkues") { nav(`/punekerkues/${r.data.id}`, { replace: true }); return; }
      setL(r.data); setImgIdx(0);
      api.get(`/listings/${r.data.id}/reviews`).then((rr) => setReviews(rr.data)).catch(() => {});
      api.get("/favorites/ids").then((f) => setSaved(f.data.includes(r.data.id))).catch(() => {});
    }).catch(() => toast.error("Shpallja nuk u gjet"));
  }, [id]);
  useEffect(() => { load(); }, [load]);
  useEffect(() => {
    if (l && window.location.hash === "#vleresimet") {
      setTimeout(() => document.getElementById("vleresimet")?.scrollIntoView({ behavior: "smooth", block: "start" }), 400);
    }
  }, [l?.id]);

  if (!l) return <div className="p-8 text-center text-slate-400">Duke ngarkuar...</div>;
  const cat = catMap[l.category] || catMap.sherbime;
  const d = l.data || {};
  const stopPoints = l.category === "udhetim"
    ? [...(d.autobus?.stops || []), ...(d.dergesa?.stops || [])].filter((s) => s.lat != null && s.lng != null).map((s) => ({ ...l, lat: s.lat, lng: s.lng, city: s.name || l.city }))
    : [];
  const images = l.images && l.images.length ? l.images : ["https://images.unsplash.com/photo-1486406146926-c627a92ad1ab?w=800"];

  const toggleSave = () => requireAuth(async () => {
    const r = await api.post(`/favorites/${l.id}`); setSaved(r.data.saved);
    toast.success(r.data.saved ? "U ruajt" : "U hoq");
  });
  const share = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) await navigator.share({ title: l.title, url });
      else { await navigator.clipboard.writeText(url); toast.success("Linku u kopjua"); }
    } catch { return; }
    if (user) api.post("/points/share").then((r) => { if (r.data.awarded) toast.success(`+${r.data.awarded} GjejKredit për shpërndarjen`); }).catch(() => {});
  };
  const navigate = () => setNavOpen(true);
  const submitReport = () => requireAuth(async () => {
    try { await api.post("/reports", { target_type: "listing", target_id: l.id, reason }); setReportOpen(false); toast.success("Raportimi u dërgua"); }
    catch (e) { toast.error(errMsg(e)); }
  });

  const contacts = CONTACTS(l);
  if (l.owner?.id && l.owner.id !== user?.id && l.allow_messages !== false) {
    contacts.unshift({ k: "chat", label: "SMS", icon: MessageSquare, cls: "bg-sky-500",
      onClick: (e) => { e.preventDefault(); requireAuth(() => nav(`/mesazhe/${l.owner.id}`)); } });
  }
  const bookHref = l.whatsapp ? `https://wa.me/${l.whatsapp.replace(/[^0-9]/g, "")}` : l.phone ? `tel:${l.phone.replace(/\s/g, "")}` : null;
  const navApps = l.lat ? [
    { k: "google", label: "Google Maps", logo: "https://cdn.simpleicons.org/googlemaps", href: `https://www.google.com/maps/dir/?api=1&destination=${l.lat},${l.lng}` },
    { k: "apple", label: "Apple Maps", logo: "https://cdn.simpleicons.org/apple/555555", href: `https://maps.apple.com/?daddr=${l.lat},${l.lng}` },
    { k: "waze", label: "Waze", logo: "https://cdn.simpleicons.org/waze", href: `https://waze.com/ul?ll=${l.lat},${l.lng}&navigate=yes` },
  ] : [];
  const menuItems = Array.isArray(d.menu) ? d.menu.flatMap((c) => (c.items || []).map((it) => ({ ...it, cat: c.name }))) : [];
  const priceText = l.price != null ? `€${Number(l.price).toLocaleString("de-DE")}${l.price_unit || ""}` : null;

  const specs = [];
  if (l.category === "banim" && !(Array.isArray(d.units) && d.units.length)) { if (d.rooms) specs.push(["Dhoma", d.rooms]); if (d.m2) specs.push(["Sipërfaqja", `${d.m2} m²`]); if (d.floor != null) specs.push(["Kati", d.floor]); specs.push(["E mobiluar", d.furnished ? "Po" : "Jo"]); specs.push(["Parking", d.parking ? "Po" : "Jo"]); }
  if (l.category === "pune") { if (d.company) specs.push(["Kompania", d.company]); if (d.job_type) specs.push(["Orari", d.job_type]); if (d.experience) specs.push(["Përvoja", d.experience]); }
  if (l.category === "dyqan") { if (d.condition) specs.push(["Gjendja", d.condition]); if (d.year) specs.push(["Viti", d.year]); if (d.km) specs.push(["Kilometrazhi", `${d.km} km`]); if (d.fuel) specs.push(["Karburanti", d.fuel]); }


  return (
    <div className="gs-fade pb-8 bg-white min-h-screen">
      {/* HERO */}
      <div className="relative overflow-hidden">
        {l.category === "sherbime" && !(l.images && l.images.length) ? (
          <div className="w-full h-44 bg-gradient-to-br from-emerald-500 to-green-600" />
        ) : (
          <HoldZoomImg src={images[imgIdx]} alt={l.title} data-testid="detail-hero-img" onClick={() => setImgIdx((imgIdx + 1) % images.length)} className="w-full h-60 object-cover cursor-pointer" />
        )}
        <button data-testid="detail-back" onClick={() => nav(-1)} className="absolute top-3 left-3 bg-black/40 text-white rounded-full p-2 backdrop-blur-sm"><ArrowLeft className="w-5 h-5" /></button>
        <div className="absolute top-3 right-3 flex gap-2">
          <button data-testid="detail-save" onClick={toggleSave} className="bg-black/40 text-white rounded-full p-2 backdrop-blur-sm"><Heart className={`w-5 h-5 ${saved ? "fill-red-500 text-red-500" : ""}`} /></button>
          <button data-testid="detail-share" onClick={share} className="bg-black/40 text-white rounded-full p-2 backdrop-blur-sm"><Share2 className="w-5 h-5" /></button>
        </div>
        {(l.category !== "sherbime" || (l.images && l.images.length > 0)) && images.length > 1 && <span data-testid="detail-img-counter" className="absolute bottom-3 right-3 bg-black/60 text-white text-xs font-semibold px-2.5 py-1 rounded-full">{imgIdx + 1}/{images.length}</span>}
      </div>

      {/* INFO */}
      <div className="px-4 -mt-10 relative">
        <HoldZoomBox data-testid="detail-logo" className="w-24 h-24 rounded-full bg-slate-900 ring-4 ring-white shadow-md flex items-center justify-center overflow-hidden">
          {d.logo ? <img src={d.logo} alt={l.title} draggable={false} style={{ transform: `scale(${d.logo_scale || 1})` }} className="w-full h-full object-cover pointer-events-none" /> : <span className="text-white text-3xl font-extrabold">{l.title?.[0]?.toUpperCase()}</span>}
        </HoldZoomBox>
        <div className="flex items-center gap-1.5 mt-3">
          <h1 className="text-2xl font-extrabold text-slate-900 leading-tight">{l.title}</h1>
          {l.verified && <BadgeCheck className="w-6 h-6 text-emerald-500 shrink-0" />}
        </div>
        {l.subcategory && <span className="inline-block mt-1 text-xs font-semibold text-slate-500 bg-slate-100 px-2.5 py-1 rounded-full">{l.subcategory}</span>}
        {l.category === "sherbime" && d.person_name && <p className="text-sm font-medium text-slate-500 mt-1">{d.person_name}</p>}
        <div className="flex items-center justify-between gap-2 mt-1.5 text-sm">
          <div className="flex items-center gap-4">
            <span className="flex items-center gap-1"><Star className="w-4 h-4 fill-yellow-400 text-yellow-400" /><b className="text-slate-800">{l.rating || "—"}</b></span>
            <span data-testid="view-count" className="flex items-center gap-1 text-slate-500"><Eye className="w-4 h-4" />{l.views || 0}</span>
          </div>
          {(() => {
            const info = openInfo(weekFrom(d));
            if (!info && d.open_now == null) return null;
            const isOpen = info ? ["open", "h24", "closing"].includes(info.state) : d.open_now;
            const label = !info ? (d.open_now ? "Hapur tani" : "Mbyllur")
              : info.state === "h24" ? "Hapur 24 orë"
              : info.state === "closing" ? `Mbyllet ${info.to}`
              : info.state === "open" ? "Hapur tani"
              : info.state === "closed" && info.later ? `Hapet ${info.from}` : "Mbyllur";
            const red = !isOpen || info?.state === "closing";
            return (
              <span data-testid="open-status" className={`shrink-0 text-sm font-bold px-3 py-1.5 rounded-full flex items-center gap-1.5 ${red ? "bg-red-50 text-red-500" : "bg-emerald-50 text-emerald-600"}`}>
                <span className={`w-2.5 h-2.5 rounded-full ${red ? "bg-red-400" : "bg-emerald-500"}`} />{label}
              </span>
            );
          })()}
        </div>
        {priceText && <p className="text-emerald-600 font-extrabold text-2xl mt-1.5">{priceText}</p>}
        <div className="flex items-center justify-between mt-1.5">
          <p className="flex items-center gap-1 text-sm text-slate-500"><MapPin className="w-4 h-4" />{l.address ? `${l.address}, ` : ""}{l.city}{l.country ? `, ${l.country}` : ""}</p>
          {l.lat && <button data-testid="navigate-button" onClick={navigate} className="flex items-center gap-1 text-sm font-semibold text-emerald-600 shrink-0"><Navigation className="w-4 h-4" />Navigo</button>}
        </div>
        {l.description && <p className="text-sm text-slate-600 leading-relaxed mt-3">{l.description}</p>}
        <ListingOffers listingId={l.id} ownerId={l.owner?.id} />
      </div>

      {l.status === "pending" && (
        <div className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm text-amber-800 flex items-center gap-2"><Clock className="w-4 h-4 shrink-0" />Kjo shpallje është në pritje për aprovim nga admini.</div>
      )}
      {l.has_pending_changes && (user?.role === "admin" || user?.role === "staff" || String(user?.id) === String(l.owner?.id)) && (
        <div data-testid="pending-changes-banner" className="mx-4 mt-3 bg-amber-50 border border-amber-200 rounded-2xl p-3 text-sm text-amber-800 flex items-center gap-2"><Clock className="w-4 h-4 shrink-0" />
          {new URLSearchParams(window.location.search).get("draft") === "1" ? "Po sheh versionin e ri që pret aprovim." : "Ndryshimet e tua presin aprovim. Deri atëherë vizitorët shohin versionin e vjetër."}
        </div>
      )}

      {/* KONTAKT */}
      {contacts.length > 0 && (
        <Section title="Kontakt">
          <div className="flex gap-2">
            {contacts.map((c) => {
              const Icon = c.icon;
              return (
                <a key={c.k} data-testid={`contact-${c.k}-button`} href={c.href || "#"} onClick={c.onClick} target={c.href ? "_blank" : undefined} rel="noreferrer" className={`${c.cls} flex-1 text-white rounded-2xl py-3 flex flex-col items-center gap-1 text-xs font-semibold active:scale-95 transition-transform`}>
                  <Icon className="w-5 h-5" />{c.label}
                </a>
              );
            })}
          </div>
        </Section>
      )}

      {/* DETAJE (non-food specs) */}
      {specs.length > 0 && (
        <Section title="Detaje">
          <div className="grid grid-cols-2 gap-2">
            {specs.map(([k, v]) => (
              <div key={k} className="bg-slate-50 rounded-xl p-3"><div className="text-xs text-slate-400">{k}</div><div className="font-semibold text-slate-800 text-sm">{String(v)}</div></div>
            ))}
          </div>
          {Array.isArray(d.benefits) && d.benefits.length > 0 && <div className="flex flex-wrap gap-2 mt-2">{d.benefits.map((s) => <span key={s} className="bg-emerald-50 text-emerald-700 text-xs font-semibold px-3 py-1.5 rounded-full">{s}</span>)}</div>}
          {d.requirements && <p className="text-sm text-slate-600 mt-2"><b>Kërkesat:</b> {d.requirements}</p>}
        </Section>
      )}

      {/* SHËRBIME */}
      {Array.isArray(d.services) && d.services.length > 0 && (
        <Section title="Shërbime">
          <div className="grid grid-cols-4 gap-2">
            {d.services.map((s) => {
              const Icon = SERVICE_ICONS[s] || Check;
              const emoji = SERVICE_EMOJI[s];
              return (
                <div key={s} className="bg-slate-50 rounded-2xl p-2.5 flex flex-col items-center justify-center gap-1.5 aspect-square">
                  {emoji ? <span className="text-2xl leading-none">{emoji}</span> : <Icon className="w-6 h-6 text-slate-700" />}
                  <span className="text-[11px] font-medium text-slate-600 text-center leading-tight">{s}</span>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {/* SHËRBIMET QË KRYEN (sherbime) */}
      {l.category === "sherbime" && Array.isArray(d.service_list) && d.service_list.length > 0 && (
        <Section title="Shërbimet që kryen">
          <div className="space-y-2">
            {d.service_list.map((s, i) => (
              <div key={i} className="flex items-center gap-2 bg-slate-50 rounded-xl px-3 py-2.5 text-sm font-medium text-slate-700"><Wrench className="w-4 h-4 text-emerald-500 shrink-0" />{s}</div>
            ))}
          </div>
        </Section>
      )}

      {l.category === "sherbime" && (priceText || d.price_type) && (
        <Section title="Çmimi">
          <div className="bg-emerald-50/60 rounded-2xl p-4">
            {priceText ? (
              <div className="flex items-baseline gap-2"><span className="text-emerald-600 text-xl font-extrabold">{priceText}</span>{d.price_type && <span className="text-sm text-slate-500 font-medium">({d.price_type})</span>}</div>
            ) : (
              <span className="text-sm font-semibold text-slate-800">{d.price_type === "Sipas marrëveshjes" || !d.price_type ? "Çmimi sipas marrëveshjes" : d.price_type}</span>
            )}
          </div>
        </Section>
      )}

      {l.category === "sherbime" && (d.at_client || d.area_type || (Array.isArray(d.areas) && d.areas.length > 0)) && (
        <Section title="Zona ku kryen shërbime">
          <div className="bg-slate-50 rounded-2xl p-4 space-y-2.5">
            {d.area_type && <div className="text-sm text-slate-600">Mbulon: <b className="text-slate-800">{d.area_type}</b></div>}
            {Array.isArray(d.areas) && d.areas.length > 0 && <div className="flex flex-wrap gap-2">{d.areas.map((a) => <span key={a} className="flex items-center gap-1 bg-white border border-slate-200 text-slate-700 text-xs font-semibold px-2.5 py-1.5 rounded-full"><MapPin className="w-3 h-3 text-emerald-500" />{a}</span>)}</div>}
            {d.at_client && <div className="flex items-center gap-2 text-sm text-emerald-700 font-semibold"><Check className="w-4 h-4" />Shërbim në lokacionin e klientit</div>}
          </div>
        </Section>
      )}

      {/* BANIM */}
      {l.category === "banim" && Array.isArray(d.units) && d.units.length > 0 && (
        <Section title="Banim">
          <div className="space-y-2">
            {d.units.map((u, i) => (
              <div key={i} className="flex gap-3 bg-white border border-slate-100 rounded-2xl p-2 shadow-sm">
                {u.image ? <div className="w-24 h-24 rounded-xl overflow-hidden shrink-0"><HoldZoomImg src={u.image} alt={u.name} className="w-24 h-24 object-cover" /></div> : <div className="w-24 h-24 rounded-xl bg-slate-100 flex items-center justify-center shrink-0"><Bed className="w-7 h-7 text-slate-300" /></div>}
                <div className="flex-1 min-w-0">
                  <div className="font-bold text-slate-800">{u.name}</div>
                  <div className="flex flex-wrap gap-x-3 gap-y-1 mt-1 text-xs text-slate-500">
                    {u.persons && <span className="flex items-center gap-1"><Users className="w-3.5 h-3.5" />{u.persons} persona</span>}
                    {u.beds && <span className="flex items-center gap-1"><Bed className="w-3.5 h-3.5" />{u.beds} krevat</span>}
                    {u.rooms && <span>{u.rooms} dhoma</span>}
                    {u.m2 && <span>{u.m2} m²</span>}
                  </div>
                  {u.price != null && u.price !== "" && <div className="text-emerald-600 font-extrabold text-lg mt-1">€{u.price} <span className="text-xs font-semibold text-slate-400">/ natë</span></div>}
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* PRODUKTE (dyqan) */}
      {l.category === "dyqan" && Array.isArray(d.products) && d.products.length > 0 && (
        <Section title="Produkte">
          <div className="grid grid-cols-2 gap-3">
            {d.products.map((p, i) => (
              <div key={i} className="bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm">
                {p.image ? <div className="overflow-hidden"><HoldZoomImg src={p.image} alt={p.name} className="w-full h-28 object-cover" /></div> : <div className="w-full h-28 bg-slate-100 flex items-center justify-center"><ShoppingBag className="w-7 h-7 text-slate-300" /></div>}
                <div className="p-2.5">
                  <div className="font-semibold text-sm text-slate-800 truncate">{p.name}</div>
                  {p.desc && <div className="text-xs text-slate-400 truncate">{p.desc}</div>}
                  <div className="flex items-center justify-between gap-1 mt-1.5">
                    {p.price ? <div className="text-emerald-600 font-extrabold text-sm">{money(p.price)}</div> : <span />}
                    {p.condition && <span className="text-[10px] bg-slate-100 text-slate-600 font-semibold px-2 py-0.5 rounded-full shrink-0">{p.condition}</span>}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </Section>
      )}

      {/* SHËRBIME (udhetim) */}
      {l.category === "udhetim" && Array.isArray(d.travel_services) && d.travel_services.length > 0 && (
        <Section title="Shërbime">
          <div className="grid grid-cols-4 gap-2">
            {d.travel_services.filter((k) => TRAVEL[k]).map((k) => {
              const t = TRAVEL[k]; const Icon = t.icon;
              return (
                <div key={k} className="bg-slate-50 rounded-2xl p-2.5 flex flex-col items-center justify-center gap-1.5 aspect-square">
                  <Icon className="w-6 h-6 text-slate-700" />
                  <span className="text-[11px] font-medium text-slate-600 text-center leading-tight">{t.label}</span>
                </div>
              );
            })}
          </div>
        </Section>
      )}

      {l.category === "udhetim" && d.autobus && (
        <TravelBox icon={Bus} title="Autobus" from={d.autobus.from} to={d.autobus.to} testid="book-autobus"
          rows={[["Data", d.autobus.date], ["Ora e nisjes", d.autobus.departure], ["Ora e mbërritjes", d.autobus.arrival], ["Çmimi", money(d.autobus.price)], ["Bagazhi", d.autobus.baggage], ["Vende të lira", d.autobus.seats], ["Operatori", d.autobus.operator]]}
          cta="Rezervo" href={bookHref} />
      )}
      {l.category === "udhetim" && d.autobus?.stops?.length > 0 && <StopsTimeline stops={d.autobus.stops} icon={Bus} tid="autobus-stops" />}
      {l.category === "udhetim" && d.aeroplan && (
        <TravelBox icon={Plane} title="Aeroplan" from={d.aeroplan.from} to={d.aeroplan.to} testid="book-aeroplan"
          rows={[["Data", d.aeroplan.date], ["Ora", d.aeroplan.time], ["Kompania ajrore", d.aeroplan.airline], ["Nr. fluturimit", d.aeroplan.flight_no], ["Bagazhi", d.aeroplan.baggage], ["Çmimi", money(d.aeroplan.price)]]}
          cta="Bli biletë" href={bookHref} />
      )}
      {l.category === "udhetim" && d.dergesa && (
        <TravelBox icon={Package} title="Dërgesa" from={d.dergesa.from} to={d.dergesa.to} testid="book-dergesa"
          rows={[["Lloji", d.dergesa.type], ["Pesha", d.dergesa.weight], ["Përmasat", d.dergesa.dimensions], ["Çmimi", money(d.dergesa.price)], ["Data e marrjes", d.dergesa.pickup_date], ["Data e dorëzimit", d.dergesa.delivery_date]]}
          badges={[d.dergesa.pickup && "Marrje nga adresa", d.dergesa.delivery && "Dorëzim në adresë"].filter(Boolean)}
          cta="Dërgo tani" href={bookHref} />
      )}
      {l.category === "udhetim" && d.dergesa?.stops?.length > 0 && <StopsTimeline stops={d.dergesa.stops} icon={Package} tid="dergesa-stops" />}
      {l.category === "udhetim" && d.taksi && (
        <TravelBox icon={Car} title="Taksi" from={d.taksi.from} to={d.taksi.to} testid="book-taksi"
          rows={[["Data", d.taksi.date], ["Ora", d.taksi.time], ["Persona", d.taksi.persons], ["Çmimi", money(d.taksi.price)], ["Zona", d.taksi.zone]]}
          cta="Rezervo taksi" href={bookHref} />
      )}

      {/* MENY */}
      {menuAt !== null && <MenuViewer items={menuItems} start={menuAt} onClose={closeMenu} />}
      {menuItems.length > 0 && bookEligible(l.category, l.subcategory) && (
        <Section title="Menyja" action="Hape të madhe" onAction={() => setBookOpen(true)}>
          <div className="pb-8 pt-1" data-testid="menu-book-inline">
            <MenuBook items={menuItems} theme={d.menu_style} logo={d.logo} title={l.title} width={Math.min(window.innerWidth - 48, 600)} onOpen={() => setBookOpen(true)} />
          </div>
        </Section>
      )}
      {bookOpen && <MenuBookViewer items={menuItems} theme={d.menu_style} logo={d.logo} title={l.title} onClose={() => setBookOpen(false)} />}
      {menuItems.length > 0 && !bookEligible(l.category, l.subcategory) && d.menu.map((c, ci) => {
        const start = d.menu.slice(0, ci).reduce((s, x) => s + (x.items || []).length, 0);
        if (!(c.items || []).length) return null;
        return (
          <Section key={ci} title={(c.name || "").trim() || "Meny"} action="Shiko të gjitha" onAction={() => setMenuAt(start)}>
            <div data-testid={`menu-cat-section-${ci}`} className="flex items-start gap-3 overflow-x-auto no-scrollbar -mx-4 px-4 pb-1">
              {c.items.map((it, ii) => (
                <div key={ii} data-testid={`menu-item-${start + ii}`} onClick={() => setMenuAt(start + ii)} className="w-32 shrink-0 bg-white rounded-2xl border border-slate-100 overflow-hidden shadow-sm cursor-pointer active:scale-[0.98] transition-transform">
                  {it.image ? <div className="overflow-hidden"><HoldZoomImg src={it.image} alt={it.name} className="w-full h-24 object-cover" /></div> : <div className="w-full h-24 bg-slate-100 flex items-center justify-center"><ShoppingBag className="w-6 h-6 text-slate-300" /></div>}
                  {(it.name || it.price) && (
                    <div className="p-2">
                      {it.name && <div className="font-semibold text-sm text-slate-800 truncate">{it.name}</div>}
                      {it.price && <div data-testid={`menu-item-price-${start + ii}`} className="text-emerald-600 font-extrabold text-sm">{/[€$£a-zA-Z]/.test(String(it.price)) ? it.price : `${it.price} €`}</div>}
                    </div>
                  )}
                </div>
              ))}
            </div>
          </Section>
        );
      })}

      {/* PAGESAT */}
      {Array.isArray(d.payment) && d.payment.length > 0 && (
        <Section title="Pagesat">
          <PaymentMethods methods={d.payment} />
        </Section>
      )}

      {/* ORARI */}
      {(d.checkin || d.checkout || d.reception) && (
        <Section title="Orari">
          <div className="bg-slate-50 rounded-2xl p-4 space-y-1.5 text-sm">
            {d.checkin && <div className="flex justify-between"><span className="text-slate-600">Check-in</span><span className="font-semibold text-slate-800">{d.checkin}</span></div>}
            {d.checkout && <div className="flex justify-between"><span className="text-slate-600">Check-out</span><span className="font-semibold text-slate-800">{d.checkout}</span></div>}
            {d.reception && <div className="flex justify-between"><span className="text-slate-600">Recepsion</span><span className="font-semibold text-slate-800">{d.reception}</span></div>}
          </div>
        </Section>
      )}
      {!d.checkin && !d.checkout && !d.reception && Array.isArray(d.hours_week) && d.hours_week.length > 0 ? (
        <Section title="Orari">
          <div className="bg-slate-50 rounded-2xl p-4">
            {groupHours(d.hours_week).map((g, i) => (
              <div key={i} className="flex items-center justify-between py-1 text-sm">
                <span className="text-slate-600 font-medium flex items-center gap-2">{i === 0 && <Clock className="w-4 h-4 text-emerald-500" />}<span className={i === 0 ? "" : "ml-6"}>{g.startDay === g.endDay ? g.startDay : `${g.startDay} – ${g.endDay}`}</span></span>
                <span className={g.open ? "text-slate-800 font-semibold" : "text-red-500 font-semibold"}>{g.label}</span>
              </div>
            ))}
          </div>
        </Section>
      ) : d.hours ? (
        <Section title="Orari"><div className="bg-slate-50 rounded-2xl p-4 flex items-center gap-2 text-sm text-slate-700"><Clock className="w-4 h-4 text-emerald-500" />{d.hours}</div></Section>
      ) : null}

      {/* LOKACIONI */}
      {l.lat && (
        <Section title="Lokacioni">
          <div className="rounded-2xl overflow-hidden border border-slate-100 shadow-sm"><MapView points={[{ ...l, category: l.category }, ...stopPoints]} height={220} /></div>
          <Button data-testid="navigate-button-map" onClick={navigate} className="w-full mt-2 rounded-xl h-11 bg-slate-900 hover:bg-slate-800 font-semibold"><Navigation className="w-4 h-4 mr-2" />Navigo</Button>
        </Section>
      )}

      <ReviewsSection l={l} reviews={reviews} setReviews={setReviews} reload={load} />

      <div className="px-4 mt-5">
        <button data-testid="report-button" onClick={() => setReportOpen(true)} className="flex items-center gap-1 text-sm text-slate-400"><Flag className="w-4 h-4" />Raporto shpalljen</button>
      </div>

      <Dialog open={navOpen} onOpenChange={setNavOpen}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader><DialogTitle>Hap me navigim</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {navApps.map((a) => (
              <a key={a.k} data-testid={`nav-${a.k}`} href={a.href} target="_blank" rel="noreferrer" onClick={() => setNavOpen(false)} className="flex items-center gap-3 border border-slate-200 rounded-2xl px-4 py-3 hover:bg-slate-50 transition-colors">
                <span className="w-10 h-10 rounded-xl bg-slate-100 flex items-center justify-center overflow-hidden">
                  <img src={a.logo} alt={a.label} className="w-6 h-6" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "https://cdn.simpleicons.org/googlemaps"; }} />
                </span>
                <span className="font-semibold text-slate-800">{a.label}</span>
                <ChevronRight className="w-4 h-4 text-slate-300 ml-auto" />
              </a>
            ))}
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={reportOpen} onOpenChange={setReportOpen}>
        <DialogContent className="max-w-sm rounded-3xl">
          <DialogHeader><DialogTitle>Raporto</DialogTitle></DialogHeader>
          <div className="space-y-2">
            {["spam", "mashtrim", "ofendim", "informacion i rremë", "përmbajtje e papërshtatshme"].map((r) => (
              <button key={r} data-testid={`report-reason-${r}`} onClick={() => setReason(r)} className={`w-full text-left capitalize px-4 py-2.5 rounded-xl border ${reason === r ? "border-emerald-400 bg-emerald-50 text-emerald-700" : "border-slate-200"}`}>{r}</button>
            ))}
            <Button data-testid="submit-report" onClick={submitReport} className="w-full rounded-xl bg-emerald-500 hover:bg-emerald-600">Dërgo raportimin</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
